import {Box, Button, Divider, Stack, Typography, useTheme} from '@mui/material';
import React from 'react';

import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import Icon from 'presentation/icons';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {formatText, truncateText} from 'presentation/utils';
import {useUploadedRefundFilesStore} from 'presentation/pages/private/refund/components/UploadRefundFiles/store/useUploadedRefundFilesStore';
import {StepPreviewEnum} from '../../types';

import {useRequestPreviewDataStore} from '../../store/useRequestPreviewDataStore';
import {useStepPreviewStore} from '../../store/useStepPreviewStore';
import {MODALITY_LABEL} from '../SelectType/constants';
import {RequestPreviewSummaryProps} from './types';

const {attachment} = translations['pt-br'].privateFlow.refund.conferencePage;
const {change} = translations['pt-br'].commons;
const {previewTitle, description, patientData} =
	translations['pt-br'].privateFlow.refund.conferencePage.requestCard;

export const RequestPreviewSummary = ({
	canChangeInfos,
	showFiles,
	showRequestNumber,
}: RequestPreviewSummaryProps) => {
	const {isMobile} = useMediaQueries();
	const theme = useTheme();

	const uploadedFiles = useUploadedRefundFilesStore(state => state.uploadedFiles);
	const requestPreviewData = useRequestPreviewDataStore(state => state.data);
	const setStep = useStepPreviewStore(state => state.setStep);

	const userName = formatText(
		requestPreviewData.beneficiary.fullSocialName || requestPreviewData.beneficiary.fullName,
	);
	const formatFileName = (name: string) =>
		isMobile
			? truncateText({
					text: name,
					containerWidth: 260,
					targetWidth: 300,
					txtWidth: 260,
					shrinkFactor: 2,
				})
			: name;

	return (
		<Stack gap={1}>
			{showRequestNumber ? (
				<Stack gap={2}>
					<Stack gap={1}>
						<Typography variant="body2" color="text.secondary" fontWeight={600}>
							{previewTitle}
						</Typography>
						<Typography variant="body1" color="text.primary">
							{`Nº ${requestPreviewData.request}`}
						</Typography>
					</Stack>
					<Divider />
				</Stack>
			) : null}

			<Stack gap={2}>
				<Stack gap={1}>
					<Stack justifyContent="space-between" alignItems="center" direction="row">
						<Typography variant="body2" color="text.secondary" fontWeight={600}>
							{patientData}
						</Typography>
						{canChangeInfos ? (
							<Button
								data-tag-id={RefundTaggingEnum.CLICK_ALTERAR_DADOS_PACIENTE_PREVIA}
								variant="text"
								size="medium"
								onClick={() => setStep(StepPreviewEnum.SERVICE_TYPE)}>
								{change}
							</Button>
						) : null}
					</Stack>
					<Box display="flex" alignItems="center" gap={1}>
						<Icon name="person-filled" size={20} color={theme.palette.primary.main} />
						<Typography variant="body1" color="text.primary">
							{userName}
						</Typography>
					</Box>
					<Box display="flex" alignItems="center" gap={1}>
						<Icon name="stethoscope" size={20} color={theme.palette.primary.main} />
						<Typography variant="body1" color="text.primary">
							{MODALITY_LABEL[requestPreviewData.modality]}
						</Typography>
					</Box>
				</Stack>

				<Stack gap={1}>
					<Typography variant="body2" color="text.secondary" fontWeight={600}>
						{description}
					</Typography>

					<Typography
						variant="body1"
						color="text.primary"
						sx={{overflowWrap: 'break-word'}}>
						{requestPreviewData.description}
					</Typography>
				</Stack>
			</Stack>

			{showFiles ? (
				<Stack gap={1} mt={1}>
					<Divider />

					<Stack justifyContent="space-between" alignItems="center" flexDirection="row">
						<Typography variant="body2" color="text.secondary" fontWeight={600}>
							{attachment}
						</Typography>
						{canChangeInfos ? (
							<Button
								data-tag-id={RefundTaggingEnum.CLICK_ALTERAR_ANEXOS_PREVIA}
								variant="text"
								size="medium"
								onClick={() => setStep(StepPreviewEnum.UPLOAD_DOCUMENTS)}>
								{change}
							</Button>
						) : null}
					</Stack>
					{uploadedFiles?.map(file => (
						<Stack key={file.id} flexDirection="row" alignContent="center" gap={1}>
							<Icon name="upload-file-filled" />
							<Typography variant="subtitle1" color="text.primary" key={file.id}>
								{formatFileName(file.filename)}
							</Typography>
						</Stack>
					))}
				</Stack>
			) : null}
		</Stack>
	);
};
